import { Guard } from "./utils";
import _ from "lodash";
import { DemographicsMotivational } from "./DemographicsMotivational";
import { LandingPage } from "./LandingPage";
import { LoadingPage } from "./features/LoadingPage";
import { PageName, SURVEY_JSON } from "./quiz.config";

const COMPONENT_CONFIG = {
  'experience': {
    nextPage: 'email',
    component: LoadingPage,
    componentName: 'LoadingPage',
  },
  'email': {
    nextPage: 'duration',
    component: DemographicsMotivational,
    componentName: 'Post-Email Page',
  }
} as const;

function isTransitionComponentKey(key: string | null): key is keyof typeof COMPONENT_CONFIG {
  return key !== null && key in COMPONENT_CONFIG;
}

enum QuizSection {
  GETTING_STARTED = 'Getting Started',
  HABITS_AND_BEHAVIORS = 'Habits & Behaviors',
  TRAINING_AND_NUTRITION = 'Training & Nutrition',
};

enum QuizHeading {
  FOUNDATIONS = 'FOUNDATIONS',
  USERS_GOAL = '{name}\'S GOAL',
  HABITS_BEHAVIORS = 'HABITS & BEHAVIORS',
  TRAINING_NUTRITION = 'TRAINING & NUTRITION',
};

type HeadingConfig = {
  [key in QuizHeading]: {
    startQuestion: PageName,
    endQuestion: PageName,
  }
};

type SectionConfig = {
  [key in QuizSection]: {
    startQuestion: PageName,
    endQuestion: PageName,
  }
}

type SectionTotal = {
  startIndex: number,
  endIndex: number,
  total: number,
}

type SectionTotals = {
  [key in QuizSection]: SectionTotal;
}

const SECTION_CONFIG: SectionConfig = {
  [QuizSection.GETTING_STARTED]: {
    startQuestion: 'goals',
    endQuestion: 'email',
  },
  [QuizSection.HABITS_AND_BEHAVIORS]: {
    startQuestion: 'duration',
    endQuestion: 'otherPrograms',
  },
  [QuizSection.TRAINING_AND_NUTRITION]: {
    startQuestion: 'workouttrackingapp',
    endQuestion: 'focus',
  },
};

const HEADING_CONFIG: HeadingConfig = {
  [QuizHeading.FOUNDATIONS]: {
    startQuestion: 'goals',
    endQuestion: 'experience',
  },
  [QuizHeading.USERS_GOAL]: {
    startQuestion: 'email',
    endQuestion: 'email',
  },
  [QuizHeading.HABITS_BEHAVIORS]: {
    startQuestion: 'duration',
    endQuestion: 'otherPrograms',
  },
  [QuizHeading.TRAINING_NUTRITION]: {
    startQuestion: 'workouttrackingapp',
    endQuestion: 'focus',
  },
};

type HeadingConfigTotals = {
  [key in QuizHeading]: SectionTotal;
}

type ValueChoice = {
  value: string,
  text: string,
};

const isValueChoice: Guard<ValueChoice> = (value: any): value is ValueChoice => {
  return typeof value === 'object'
    && 'value' in value && typeof value.value === 'string'
    && 'text' in value && typeof value.text === 'string';
}

const findPageIndex = (name: string) => {
  let count = 0;

  for (const page of SURVEY_JSON.pages) {
    if (page.name === name) return count;
    const component = COMPONENT_CONFIG[page.name as keyof typeof COMPONENT_CONFIG];
    if (component) {
      count++;
      if (component.componentName === name) return count;
    }
    count++;
  }

  return -1;
}

const SECTION_QUESTION_COUNT: SectionTotals = _.entries(SECTION_CONFIG).reduce((acc, [section, { startQuestion, endQuestion }]) => {
  const startIndex = findPageIndex(startQuestion)
  const endIndex = findPageIndex(endQuestion);
  acc[section as QuizSection] = {
    startIndex,
    endIndex,
    total: endIndex - startIndex + 1,
  }
  return acc;
}, {} as SectionTotals);

const SECTION_QUESTION_COUNT_HEADINGS: HeadingConfigTotals = _.entries(HEADING_CONFIG).reduce((acc, [section, { startQuestion, endQuestion }]) => {
  const startIndex = findPageIndex(startQuestion)
  const endIndex = findPageIndex(endQuestion);
  acc[section as QuizHeading] = {
    startIndex,
    endIndex,
    total: endIndex - startIndex + 1,
  }
  return acc;
}, {} as HeadingConfigTotals);

const getSectionInfo = (section: QuizSection): SectionTotal => SECTION_QUESTION_COUNT[section];

const getQuestionSection = (questionName: string): QuizSection | undefined =>
  _.findKey(SECTION_CONFIG, (_value, section) => {
    const { startIndex, endIndex } = SECTION_QUESTION_COUNT[section as QuizSection];
    const questionIndex = findPageIndex(questionName);
    return questionIndex >= startIndex && questionIndex <= endIndex;
  }) as QuizSection | undefined;

const getQuestionHeading = (questionName: string): QuizHeading | undefined =>
  _.findKey(HEADING_CONFIG, (_value, section) => {
    const { startIndex, endIndex } = SECTION_QUESTION_COUNT_HEADINGS[section as QuizHeading];
    const questionIndex = findPageIndex(questionName);
    return questionIndex >= startIndex && questionIndex <= endIndex;
  }) as QuizHeading | undefined;

  
export {
  isValueChoice,
  findPageIndex,
  getQuestionSection,
  getQuestionHeading,
  getSectionInfo,
  COMPONENT_CONFIG,
  isTransitionComponentKey,
  QuizSection,
  SECTION_CONFIG,
  SURVEY_JSON,
};

export type {
  SectionConfig,
  SectionTotals,
}
